import { useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DoorType, MobileOpCode } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { getTranslatedReason } from 'utils/AccessEvent/AccessEvent';
import { dataGridIdSlots } from 'v2/components/DataGrid/dataGridIdSlots';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import LockIconNameCell from 'v2/components/LockIconNameCell/LockIconNameCell';
import StatusChip from 'v2/components/StatusChip/StatusChip';
import { TextCell, TwoLineCell } from 'v2/components/TextCell/TextCell';
import { DATAGRID_ROW_HEIGHT, EMPTY_VALUE_PLACEHOLDER } from 'v2/constants';
import { PAGE_SIZE } from 'v2/hooks/useAccessEvents/useAccessEvents';
import { formatDate } from 'v2/utils/dateTime';

export interface AccessEventRow {
  id: string;
  createdAt: string;
  action: MobileOpCode;
  reason: string | null | undefined;
  visitorName?: string;
  visitorEmail?: string;
  doorName?: string;
  doorType?: DoorType | null;
}

interface Props {
  rows: AccessEventRow[];
  loading: boolean;
  className?: string;
  hideUserColumn?: boolean;
  hideDeviceColumn?: boolean;
  rowCount?: number;
  page?: number;
  onPageChange?: (page: number) => void;
}

const AccessEventsDataGrid: React.FC<Props> = ({
  rows,
  loading,
  className,
  hideUserColumn = false,
  hideDeviceColumn = false,
  rowCount,
  page,
  onPageChange
}) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isPaginated = onPageChange !== undefined;
  const headerWithId = renderHeaderWithId();

  const dateTimeColumn: GridColDef<AccessEventRow> = {
    field: 'createdAt',
    headerName: translation.date_time,
    flex: 1,
    minWidth: 100,
    sortable: false,
    hideSortIcons: true,
    renderHeader: headerWithId,
    renderCell: ({ row }) => (
      <TwoLineCell
        title={formatDate(row.createdAt, 'timeOnlyLong')}
        content={formatDate(row.createdAt, 'dateOnlyLong')}
      />
    )
  };

  const userColumn: GridColDef<AccessEventRow> = {
    field: 'visitorName',
    headerName: translation.user,
    flex: 2,
    sortable: false,
    hideSortIcons: true,
    renderHeader: headerWithId,
    valueGetter: (_, row) => row.visitorName,
    renderCell: ({ row }) => <TwoLineCell title={row.visitorName ?? ''} content={row.visitorEmail ?? ''} />
  };

  const deviceColumn: GridColDef<AccessEventRow> = {
    field: 'doorName',
    headerName: translation.device,
    flex: 1.5,
    sortable: false,
    hideSortIcons: true,
    renderHeader: headerWithId,
    valueGetter: (_, row) => row.doorName,
    renderCell: ({ row }) => <LockIconNameCell door={{ name: row.doorName ?? '', type: row.doorType ?? undefined }} />
  };

  const accessColumn: GridColDef<AccessEventRow> = {
    field: 'action',
    headerName: translation.access,
    flex: 0.9,
    minWidth: isLgUp ? 180 : undefined,
    sortable: false,
    hideSortIcons: true,
    renderHeader: headerWithId,
    renderCell: ({ row }) => {
      const granted = row.action === MobileOpCode.AccessGranted;
      return (
        <StatusChip
          variant={granted ? 'complete' : 'incomplete'}
          label={granted ? translation.access_granted : translation.access_denied}
          iconOnly={!isLgUp}
        />
      );
    }
  };

  const detailsColumn: GridColDef<AccessEventRow> = {
    field: 'reason',
    headerName: translation.details,
    flex: 1.5,
    sortable: false,
    hideSortIcons: true,
    renderHeader: headerWithId,
    renderCell: ({ row }) => (
      <TextCell>{getTranslatedReason(row.reason ?? EMPTY_VALUE_PLACEHOLDER, translation)}</TextCell>
    )
  };

  const columns = [
    dateTimeColumn,
    ...(!hideUserColumn ? [userColumn] : []),
    ...(!hideDeviceColumn ? [deviceColumn] : []),
    accessColumn,
    detailsColumn
  ];

  const paginationProps =
    isPaginated && rowCount !== undefined && page !== undefined
      ? {
          paginationMode: 'server' as const,
          rowCount,
          paginationModel: { page, pageSize: PAGE_SIZE },
          onPaginationModelChange: (model: { page: number; pageSize: number }) => onPageChange(model.page),
          pageSizeOptions: [PAGE_SIZE]
        }
      : { hideFooter: true };

  return (
    <DataGrid
      className={className}
      rows={rows}
      getRowId={(row) => row.id}
      columns={columns}
      slots={dataGridIdSlots}
      initialState={{ sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] } }}
      autoHeight
      rowHeight={DATAGRID_ROW_HEIGHT}
      loading={loading}
      disableRowSelectionOnClick
      disableColumnMenu
      slotProps={{
        loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
        basePagination: { material: { labelDisplayedRows: ({ from, to }) => `${from}–${to}` } }
      }}
      {...paginationProps}
    />
  );
};

export default AccessEventsDataGrid;
